import { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Loader, TypographyV2 } from '@which/seatbelt'
import { ChevronLeftIcon, CrossIcon } from '@which/seatbelt/src/components/Icons'
import { dynamicGa4DataLayerPush } from '@which/shared'

import classnames from 'classnames'

import { useCategoryFilterQuery } from '../../../hooks/useCategoryFilterQuery/useCategoryFilterQuery'
import { ShowAlternativesButton } from '../Button/ShowAlternativesButton'
import { useLaptopHelperItemSpec } from '../hooks/useLaptopHelperItemSpec'
import { NoResults } from '../ResultsScreen/NoResults'
import { ResultsScreen } from '../ResultsScreen/ResultsScreen'
import type { LaptopHelperStep } from '../Steps/useLaptopQuestionSteps'
import { useLaptopQuestionSteps } from '../Steps/useLaptopQuestionSteps'
import styles from './LaptopHelperNavigation.module.scss'
import { useLaptopHelperNavigation } from './useLaptopHelperNavigation'

const CATEGORY_SLUG = 'laptops'

type LaptopHelperNavigationProps = {
  handleClose: () => void
}

// eslint-disable-next-line @typescript-eslint/ban-types
export type LaptopHelperState = {}

export const LaptopHelperNavigation = ({ handleClose }: LaptopHelperNavigationProps) => {
  const { steps: questionSteps, filters } = useLaptopQuestionSteps()
  const itemSpec = useLaptopHelperItemSpec()
  const [isClosing, setIsClosing] = useState(false)

  const { productsWithPagination, totalProductCount, loading, error } = useCategoryFilterQuery({
    categorySlug: CATEGORY_SLUG,
    basicFilterData: filters,
  })
  const dialogRef = useRef<HTMLDialogElement>(null)
  useEffect(() => {
    if (dialogRef.current) {
      dialogRef.current.showModal()
    }
  }, [])

  const resultComponent = useCallback(() => {
    if (loading) {
      return null
    }

    const products = productsWithPagination?.products ?? []

    if (error || products.length === 0) {
      return <NoResults categorySlug={CATEGORY_SLUG} />
    }

    return <ResultsScreen categorySlug={CATEGORY_SLUG} products={products} />
  }, [loading, error, productsWithPagination])

  const resultsStep: LaptopHelperStep = {
    component: resultComponent,
    title: 'Our recommendation',
  }

  const goToLastPageButtonText = `See ${totalProductCount} laptops`

  const navigation = useLaptopHelperNavigation([...questionSteps, resultsStep])

  const onNextButtonClick = () => {
    navigation.trackingCallback?.({
      item_text: 'Next',
    })
    navigation.next()
  }

  const onGoToLastPageClick = () => {
    dynamicGa4DataLayerPush({
      event: 'personalisation_tool_action',
      action_group: 'action',
      item_text: goToLastPageButtonText,
      item_spec: itemSpec,
      item_group: 'personalisation tools',
      tool_name: 'Laptop picker helper',
      item_personalisation: 'false',
    })
    navigation.goToLastPage()
  }

  const handleCloseRequest = () => {
    setIsClosing(true)
  }

  const handleAnimationEnd = () => {
    if (isClosing) {
      handleClose()
    }
  }

  return (
    <dialog
      className={classnames(styles.root, { [styles.closing]: isClosing })}
      ref={dialogRef}
      onAnimationEnd={handleAnimationEnd}
    >
      <div className={styles.topRow}>
        <button
          onClick={() => {
            if (navigation.canGoBack) {
              navigation.back()
            }
          }}
          type="button"
          title="Back"
          aria-label="back"
          aria-disabled={!navigation.canGoBack}
          disabled={!navigation.canGoBack}
          className={classnames(styles.backButton, { [styles.disabled]: !navigation.canGoBack })}
        >
          <ChevronLeftIcon />
        </button>
        <TypographyV2 textStyle="sb-text-interface-heading-medium" className={styles.title}>
          {navigation.title}
        </TypographyV2>
        <button
          onClick={handleCloseRequest}
          type="button"
          title="Close"
          aria-label="close"
          className={styles.closeButton}
        >
          <CrossIcon />
        </button>
      </div>
      <div className={styles.content}>{navigation.component && <navigation.component />}</div>
      <div className={styles.bottomRow}>
        {navigation.isLastPage ? (
          totalProductCount > 0 && (
            <ShowAlternativesButton
              filters={filters}
              data-item-parent-text={navigation.title}
              data-item-index="1"
              data-item-group="personalisation tools"
              data-tool-name="Laptop picker helper"
              data-item-personalisation="false"
              data-which-id="personalisation-tool-onward-journey"
            />
          )
        ) : (
          <>
            <Button
              onClick={onGoToLastPageClick}
              appearance="secondary"
              disabled={totalProductCount === 0 && !loading}
              aria-busy={loading}
            >
              {loading ? <Loader aria-hidden="true" /> : goToLastPageButtonText}
            </Button>
            <Button onClick={onNextButtonClick} disabled={!navigation.canGoNext}>
              Next
            </Button>
          </>
        )}
      </div>
    </dialog>
  )
}
